import cn from "classnames";
import Datetime from "components/shared/datetime";
import { GroupedCarSelect } from "components/shared/form";
import Select, { customFilterSelectStyles } from "components/shared/select";
import { utcTimeInOffset } from "helpers/date";
import {
    bidPointTypesEnum,
    bidStateFlagsEnum,
    bidStatusesEnum,
    featureModuleTypesEnum,
    sourceTypesEnum,
} from "helpers/enums";
import { GEOZONE_FIELDS_BY_REGION_TYPES } from "helpers/map";
import { mergeFilters } from "helpers/reqparams";
import debounce from "lodash/debounce";
import upperFirst from "lodash/upperFirst";
import { ITEM_ROOT } from "odata-query";
import React, { Component } from "react";
import { getContractsForFilter } from "sources/books/counterpartyContracts";
import { count as getCountOfLegalPersons } from "sources/books/legalPersons";
import { buildStatusFilter } from "components/waybills/status";
import * as configs from "./config";

const mustHaveFalseKeys = ["isDeleted"];

const initStateValue = {
    emptyMileage: { key: "ge", value: "" },
    outputWithEmptyMileage: { key: "ge", value: "" },
};

const normalizePointFilter = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const normalizeIds = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const getBidPointCounterpartyIds = (value) => (value?.counterpartyIds ? value.counterpartyIds : normalizeIds(value));

const appendAndFilter = (filter, condition) => ({
    ...filter,
    and: [...(Array.isArray(filter.and) ? filter.and : filter.and ? [filter.and] : []), condition],
});

const buildBidPointCounterpartyFilter = (type, value) => {
    const counterpartyIds = getBidPointCounterpartyIds(value);
    return {
        bidPoints: {
            any: {
                type,
                counterpartyId: counterpartyIds.length ? { in: counterpartyIds } : -1,
            },
        },
    };
};

const buildPointRegionFilters = (pointPath, value) => {
    const filtersByField = normalizePointFilter(value).reduce((memo, { id, type }) => {
        const field = GEOZONE_FIELDS_BY_REGION_TYPES[type];
        if (id == null || !field) {
            return memo;
        }
        memo[field] = [...(memo[field] || []), id];
        return memo;
    }, {});

    return Object.keys(filtersByField).map((field) => ({
        [`${pointPath}/${field}`]: { in: filtersByField[field] },
    }));
};

export default class BidsFilter extends Component {
    constructor(props) {
        super(props);
        this.onIdInputChange = debounce(this.onIdInputChange, 500);
        this.onRadiusInputChange = debounce(this.onRadiusInputChange, 500);
        this.onContractNumberInputChange = debounce(this.onContractNumberInputChange, 500);
        this.onEmptyMileageInputChange = debounce(this.onEmptyMileageInputChange, 500);
        this.onOutputWithEmptyMileageInputChange = debounce(this.onOutputWithEmptyMileageInputChange, 500);
        const getData = (name) => {
            const filterValue = this.filterValue(name);
            const key = Object.keys(filterValue)[0];
            const value = Object.values(filterValue)[0];
            if (!value) {
                return initStateValue[name];
            }
            return { key, value };
        };
        this.state = {
            idInputValue: this.filterValue("id"),
            contractNumberInputValue: this.filterValue("contractNumber").query,
            outputWithEmptyMileage: getData("outputWithEmptyMileage"),
            emptyMileage: getData("emptyMileage"),
        };
    }

    componentDidMount() {
        getCountOfLegalPersons().then((data) => {
            this.setState({ legalPersonCount: data.count });
        });
    }

    UNSAFE_componentWillMount() {
        const { carId, trailerId, params } = this.props;
        if (carId || trailerId) {
            const { mergeParams, params } = this.props;
            params.customFilters = {};
            params.preparedFilter = {};
            if (carId) {
                params.customFilters.car = [Number(carId)];
                params.preparedFilter.carIds = { any: { [ITEM_ROOT]: { in: [Number(carId)] } } };
            }
            if (trailerId) {
                params.customFilters.trailerId = [Number(trailerId)];
                params.preparedFilter.trailerIds = { any: { [ITEM_ROOT]: { in: [Number(trailerId)] } } };
            }
            mergeFilters(mergeParams, { name: "custom", value: null }, params);
        } else if (params.customFilters && params.customFilters.approveStatus) {
            this.resetFilters();
        }
    }

    get tags() {
        const { showFullAddress, showFactDates, roles, profile } = this.props;
        const { availableFeatureModules } = profile;
        const isSelected = (name) => this.filterValue(name);
        return [
            {
                name: "С моими машинами",
                selected: isSelected("isMyBid"),
                show: true,
                onChange: this.mergeFilters.bind(this, { name: "isMyBid", value: !isSelected("isMyBid") }),
            },
            {
                name: "Отображать стоимость с учётом затрат",
                selected: isSelected("showCostAwareValues"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "showCostAwareValues",
                    value: !isSelected("showCostAwareValues"),
                }),
            },
            {
                name: "Созданные мной",
                selected: isSelected("isCreatedByMe"),
                show: true,
                onChange: this.mergeFilters.bind(this, { name: "isCreatedByMe", value: !isSelected("isCreatedByMe") }),
            },
            {
                name: "С опозданием",
                selected: isSelected("estimations"),
                show: true,
                onChange: this.mergeFilters.bind(this, { name: "estimations", value: !isSelected("estimations") }),
            },
            {
                name: "Порожние",
                selected: isSelected("isEmptyMileageBid"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "isEmptyMileageBid",
                    value: !isSelected("isEmptyMileageBid"),
                }),
            },
            {
                name: "Только сборные заявки",
                selected: isSelected("isCompoundBid"),
                show: true,
                onChange: this.mergeFilters.bind(this, { name: "isCompoundBid", value: !isSelected("isCompoundBid") }),
            },
            {
                name: "Экспресс-заявки",
                selected: isSelected("isExpressBid"),
                show: true,
                onChange: this.mergeFilters.bind(this, { name: "isExpressBid", value: !isSelected("isExpressBid") }),
            },
            {
                name: "Согласованные",
                selected: isSelected("isApproved"),
                disabled: isSelected("isUnapproved"),
                show: this.props.organization.usesBidsApproval,
                onChange: this.mergeFilters.bind(this, { name: "isApproved", value: !isSelected("isApproved") }),
            },
            {
                name: "Несогласованные",
                selected: isSelected("isUnapproved"),
                disabled: isSelected("isApproved"),
                show: this.props.organization.usesBidsApproval,
                onChange: this.mergeFilters.bind(this, { name: "isUnapproved", value: !isSelected("isUnapproved") }),
            },
            {
                name: "Показывать полный адрес",
                selected: showFullAddress,
                show: true,
                onChange: this.props.toggleFullAddress,
            },
            {
                name: "Удалённые",
                selected: isSelected("isDeleted"),
                show: roles.isTruckingOrganizationAdmin || roles.isRoot,
                onChange: this.mergeFilters.bind(this, { name: "isDeleted", value: !isSelected("isDeleted") }),
            },
            {
                name: "Только с перецепками/пересменками",
                selected: isSelected("hasItemsChange"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "hasItemsChange",
                    value: !isSelected("hasItemsChange"),
                }),
            },
            {
                name: "Показывать фактические даты",
                selected: showFactDates,
                show: true,
                onChange: this.props.toggleFactDates,
            },
            {
                name: "Факторинг заявки",
                selected: isSelected("hasFactoring"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.Factoring),
                onChange: this.mergeFilters.bind(this, { name: "hasFactoring", value: !isSelected("hasFactoring") }),
            },
            {
                name: "Указана температура",
                selected: isSelected("indicatedTemperature"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "indicatedTemperature",
                    value: !isSelected("indicatedTemperature"),
                }),
            },
            {
                name: "Отклонения температуры",
                selected: isSelected("temperatureRegimeDeviated"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.TemperatureRegime),
                onChange: this.mergeFilters.bind(this, {
                    name: "temperatureRegimeDeviated",
                    value: !isSelected("temperatureRegimeDeviated"),
                }),
            },
            {
                name: "Без дат выезда из последней точки",
                selected: isSelected("withoutLeftFromLastPoint"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "withoutLeftFromLastPoint",
                    value: !isSelected("withoutLeftFromLastPoint"),
                }),
            },
            {
                name: "С внешними заказами",
                selected: isSelected("withExternalBids"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.Forwarding),
                onChange: this.mergeFilters.bind(this, {
                    name: "withExternalBids",
                    value: !isSelected("withExternalBids"),
                }),
            },
            {
                name: "С сервисными точками",
                selected: isSelected("hasServicePoints"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.Exploitation),
                onChange: this.mergeFilters.bind(this, {
                    name: "hasServicePoints",
                    value: !isSelected("hasServicePoints"),
                }),
            },
            {
                name: "ЭТрН",
                selected: isSelected("hasWaybills"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.Waybills),
                onChange: this.mergeFilters.bind(this, { name: "hasWaybills", value: !isSelected("hasWaybills") }),
            },
            {
                name: "Отобразить адреса промежуточных точек",
                selected: isSelected("showAllPointAddresses"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "showAllPointAddresses",
                    value: !isSelected("showAllPointAddresses"),
                }),
            },
            {
                name: "Отобразить логиста и колонну",
                selected: isSelected("showLogistColumn"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "showLogistColumn",
                    value: !isSelected("showLogistColumn"),
                }),
            },
            {
                name: "Парные экипажи",
                selected: isSelected("withPairedCrew"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.PairedCrew),
                onChange: this.mergeFilters.bind(this, {
                    name: "withPairedCrew",
                    value: !isSelected("withPairedCrew"),
                }),
            },
            {
                name: "Отобразить организацию",
                selected: isSelected("showOrganizationColumn"),
                show: this.state.legalPersonCount >= 2,
                onChange: this.mergeFilters.bind(this, {
                    name: "showOrganizationColumn",
                    value: !isSelected("showOrganizationColumn"),
                }),
            },
            {
                name: "Закрытые вручную",
                selected: isSelected("isClosedByLogist"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "isClosedByLogist",
                    value: !isSelected("isClosedByLogist"),
                }),
            },
            {
                name: "Точка заявки скорректирована автоматически",
                selected: isSelected("hasAutoCorrectedPoint"),
                show: availableFeatureModules.includes(featureModuleTypesEnum.BidPointsAdjustment),
                onChange: this.mergeFilters.bind(this, {
                    name: "hasAutoCorrectedPoint",
                    value: !isSelected("hasAutoCorrectedPoint"),
                }),
            },
            {
                name: "Предзаявка",
                selected: isSelected("isPreBid"),
                show: true,
                onChange: this.mergeFilters.bind(this, { name: "isPreBid", value: !isSelected("isPreBid") }),
            },
            {
                name: "Отобразить заявки ниже рынка",
                selected: isSelected("belowMarketRate"),
                show: true,
                onChange: this.mergeFilters.bind(this, {
                    name: "belowMarketRate",
                    value: !isSelected("belowMarketRate"),
                }),
            },
        ];
    }

    onIdInputChange = (value) => this.mergeFilters({ name: "id", value });

    onRadiusInputChange = (name, value) => this.mergeFilters({ name, value });

    onContractNumberInputChange = (value) => {
        getContractsForFilter(value).then((data) => {
            const ids = data.map((item) => item.id);
            this.mergeFilters({ name: "contractNumber", value: { query: value, ids } });
        });
    };

    onEmptyMileageInputChange = (value) => this.mergeFilters({ name: "emptyMileage", value });

    onOutputWithEmptyMileageInputChange = (value) => this.mergeFilters({ name: "outputWithEmptyMileage", value });

    filterValue = (name) => this.props.filters[name] || "";

    renderTagInputs = () => this.tags.filter((tag) => tag.show).map(this.renderTagItem);

    buildOdataFilter = (newFilters) => {
        const { utcOffset } = this.props.organizationTimezone;
        return Object.keys(newFilters).reduce(
            (memo, filterKey) => {
                if (!newFilters[filterKey] && !mustHaveFalseKeys.includes(filterKey)) {
                    return memo;
                }
                switch (filterKey) {
                    case "emptyMileage":
                    case "outputWithEmptyMileage":
                    case "isMyBid":
                    case "isCreatedByMe":
                    case "isEmptyMileageBid":
                    case "hasHiredCar":
                    case "isMandatory":
                    case "isExpressBid":
                    case "isCompoundBid":
                    case "hasItemsChange":
                    case "hasFactoring":
                    case "isDeleted":
                    case "hasServicePoints":
                    case "hasWaybills":
                    case "isClosedByLogist":
                    case "isPreBid":
                    case "isShippingOrder":
                        memo.filter = { ...memo.filter, [filterKey]: newFilters[filterKey] };
                        return memo;
                    case "isPriorityCounterparty":
                    case "withExternalBids":
                    case "showAllPointAddresses":
                    case "showLogistColumn":
                    case "showOrganizationColumn":
                    case "skipCheckPlanEnterDate":
                    case "from.id":
                    case "to.id":
                    case "from.radius":
                    case "to.radius":
                        memo.query = { ...memo.query, [filterKey]: newFilters[filterKey] };
                        return memo;
                    case "lastSyncStatus":
                    case "counterpartyId":
                    case "hiredCarCounterpartyId":
                    case "trailerTypeId":
                    case "legalPersonId":
                    case "carLogistId":
                    case "responsibleId":
                    case "salesManagerId":
                    case "createdById":
                    case "payment/paymentStatus":
                        memo.filter[filterKey] = { in: newFilters[filterKey] };
                        return memo;
                    case "from": {
                        const filters = buildPointRegionFilters("firstBidPoint/geozone", newFilters[filterKey]);
                        if (filters.length === 1) {
                            memo.filter = { ...memo.filter, ...filters[0] };
                        } else if (filters.length > 1) {
                            memo.filter = appendAndFilter(memo.filter, { or: filters });
                        }
                        return memo;
                    }
                    case "to": {
                        const filters = buildPointRegionFilters("lastBidPoint/geozone", newFilters[filterKey]);
                        if (filters.length === 1) {
                            memo.filter = { ...memo.filter, ...filters[0] };
                        } else if (filters.length > 1) {
                            memo.filter = appendAndFilter(memo.filter, { or: filters });
                        }
                        return memo;
                    }
                    case "loadPointCounterpartyId":
                        memo.filter = appendAndFilter(
                            memo.filter,
                            buildBidPointCounterpartyFilter(bidPointTypesEnum.LoadPoint, newFilters[filterKey]),
                        );
                        return memo;
                    case "unloadPointCounterpartyId":
                        memo.filter = appendAndFilter(
                            memo.filter,
                            buildBidPointCounterpartyFilter(bidPointTypesEnum.UnloadPoint, newFilters[filterKey]),
                        );
                        return memo;
                    case "id":
                        memo.filter = {
                            ...memo.filter,
                            or: [
                                ...(memo.filter.or || []),
                                {
                                    "cast(Id, Model.String)": { contains: newFilters[filterKey] },
                                },
                                {
                                    externalId: { contains: newFilters[filterKey] },
                                },
                            ],
                        };
                        return memo;
                    case "contractNumber":
                        if (!newFilters[filterKey].query) {
                            return memo;
                        }
                        memo.filter = {
                            ...memo.filter,
                            or: [
                                ...(memo.filter.or || []),
                                {
                                    contractId: { in: newFilters[filterKey].ids.slice(0, 5) },
                                },
                                {
                                    contractNumber: { contains: newFilters[filterKey].query },
                                },
                            ],
                        };
                        return memo;
                    case "loadFrom":
                        memo.filter["firstBidPoint/planEnterDateOffset"] = {
                            ...memo.filter["firstBidPoint/planEnterDateOffset"],
                            ge: new Date(utcTimeInOffset(newFilters[filterKey], utcOffset)),
                        };
                        return memo;
                    case "loadBefore":
                        memo.filter["firstBidPoint/planEnterDateOffset"] = {
                            ...memo.filter["firstBidPoint/planEnterDateOffset"],
                            le: new Date(utcTimeInOffset(newFilters[filterKey], utcOffset)),
                        };
                        return memo;
                    case "unloadFrom":
                        memo.filter["lastBidPoint/planEnterDateOffset"] = {
                            ...memo.filter["lastBidPoint/planEnterDateOffset"],
                            ge: new Date(utcTimeInOffset(newFilters[filterKey], utcOffset)),
                        };
                        return memo;
                    case "unloadBefore":
                        memo.filter["lastBidPoint/planEnterDateOffset"] = {
                            ...memo.filter["lastBidPoint/planEnterDateOffset"],
                            le: new Date(utcTimeInOffset(newFilters[filterKey], utcOffset)),
                        };
                        return memo;
                    case "trailerId":
                        memo.filter.trailerIds = { any: { [ITEM_ROOT]: { in: newFilters[filterKey] } } };
                        return memo;
                    case "car":
                        memo.filter.carIds = { any: { [ITEM_ROOT]: { in: newFilters[filterKey] } } };
                        return memo;
                    case "transportColumn": {
                        const val = newFilters[filterKey];
                        memo.filter["car/transportColumnId"] = Array.isArray(val) ? { in: val } : val;
                        return memo;
                    }
                    case "logistId":
                        memo.filter = {
                            ...memo.filter,
                            and: [
                                ...(memo.filter.and || []),
                                {
                                    or: [
                                        { carLogistId: { in: newFilters[filterKey] } },
                                        { responsibleId: { in: newFilters[filterKey] } },
                                        { salesManagerId: { in: newFilters[filterKey] } },
                                    ],
                                },
                            ],
                        };
                        return memo;
                    case "driverId":
                        memo.filter.driverIds = { any: { [ITEM_ROOT]: { in: newFilters[filterKey] } } };
                        return memo;
                    case "estimations":
                        delete newFilters.status;
                        memo.filter = { ...memo.filter, ...configs.bidFilterValues[filterKey] };
                        return memo;
                    case "isUnapproved":
                        delete newFilters.status;
                        memo.filter = { ...memo.filter, isApproved: false, status: { in: [bidStatusesEnum.New] } };
                        return memo;
                    case "isApproved":
                        delete newFilters.status;
                        memo.filter = { ...memo.filter, isApproved: true, status: { in: [bidStatusesEnum.New] } };
                        return memo;
                    case "isExternalDistributionBid":
                        memo.filter = { ...memo.filter, sourceType: { in: [sourceTypesEnum.Forwarding] } };
                        return memo;
                    case "indicatedTemperature":
                        memo.filter = { ...memo.filter, temperatureMinimum: { ne: null } };
                        return memo;
                    case "withoutLeftFromLastPoint":
                        memo.filter = {
                            ...memo.filter,
                            status: bidStatusesEnum.Done,
                            "lastBidPoint/autoLeftAt": null,
                            "lastBidPoint/loadUnloadedAtByLogist": null,
                            "lastBidPoint/loadUnloadedAt": null,
                        };
                        return memo;
                    case "distributionBidsStatus": {
                        memo.filter = {
                            ...memo.filter,
                            ...configs.distributionBidFilterValues.status[newFilters.distributionBidsStatus],
                        };
                        const filter = {
                            and: [
                                ...(memo.filter.and || []),
                                {
                                    or: newFilters[filterKey].map(
                                        (val) => configs.distributionBidFilterValues.status[val],
                                    ),
                                },
                            ],
                        };

                        memo.filter = { ...memo.filter, ...filter };
                        return memo;
                    }
                    case "withPairedCrew":
                        memo.filter = {
                            ...memo.filter,
                            secondaryDriverId: newFilters[filterKey] ? { ne: null } : null,
                        };
                        return memo;
                    case "acceptedByDriver":
                        memo.filter = {
                            ...memo.filter,
                            acceptedByDriverAt: newFilters[filterKey] === "true" ? { ne: null } : null,
                        };
                        return memo;
                    case "status": {
                        const filter = {
                            and: [
                                ...(memo.filter.and || []),
                                {
                                    or: newFilters[filterKey].map((val) => ({
                                        status: { in: configs.bidFilterValues.status[val] },
                                        ...configs.bidFilterValues.statusExtendFilter[val],
                                    })),
                                },
                            ],
                        };

                        memo.filter = { ...memo.filter, ...filter };
                        return memo;
                    }
                    case "bidPointLoadUnloadStatus": {
                        const filter = {
                            and: [
                                ...(memo.filter.and || []),
                                {
                                    bidPointLoadUnloadStatus: { in: newFilters[filterKey] },
                                    status: bidStatusesEnum.Started,
                                },
                            ],
                        };

                        memo.filter = { ...memo.filter, ...filter };
                        return memo;
                    }
                    case "temperatureRegimeDeviated":
                        memo.filter = {
                            ...memo.filter,
                            and: [
                                ...(memo.filter.and || []),
                                {
                                    or: [
                                        { stateFlags: { has: bidStateFlagsEnum.CarTemperatureRegimeDeviated } },
                                        { stateFlags: { has: bidStateFlagsEnum.TrailerTemperatureRegimeDeviated } },
                                    ],
                                },
                            ],
                        };
                        return memo;
                    case "hasAutoCorrectedPoint":
                        memo.filter = {
                            ...memo.filter,
                            and: [
                                ...(memo.filter.and || []),
                                { stateFlags: { has: bidStateFlagsEnum.HasAutoCorrectedPoint } },
                            ],
                        };
                        return memo;
                    case "waybillStatus":
                    case "waybillSignatureRequired": {
                        const hasWaybillStatus = Array.isArray(newFilters.waybillStatus)
                            ? newFilters.waybillStatus.length > 0
                            : !!newFilters.waybillStatus;
                        if (filterKey === "waybillSignatureRequired" && hasWaybillStatus) {
                            return memo;
                        }
                        if (filterKey === "waybillStatus" && !hasWaybillStatus) {
                            return memo;
                        }
                        const status = hasWaybillStatus ? newFilters.waybillStatus : "all";
                        const signatureRequired = newFilters.waybillSignatureRequired || "all";
                        const waybillFilter = buildStatusFilter({ status, signatureRequired });
                        if (Object.keys(waybillFilter).length > 0) {
                            memo.filter = appendAndFilter(memo.filter, {
                                waybills: { any: waybillFilter },
                            });
                        }
                        return memo;
                    }
                    case "titleSignAwaiting":
                        memo.filter = {
                            ...memo.filter,
                            and: [
                                ...(memo.filter.and || []),
                                {
                                    or: [
                                        { titleSignAwaiting: newFilters[filterKey] },
                                        { stateFlags: { has: bidStateFlagsEnum.WaybillTitleSignRequired } },
                                    ],
                                },
                            ],
                        };
                        return memo;
                    case "hasUploadedFile": {
                        newFilters[filterKey].forEach((key) => {
                            let value = true;
                            if (key.startsWith("without")) {
                                key = key.replace("without", "has");
                                value = false;
                            }
                            memo.filter[key] = value;
                        });
                        return memo;
                    }
                    case "belowMarketRate": {
                        memo.filter = {
                            ...memo.filter,
                            "(AverageRate sub AverageRateByDestination)": { lt: 0 },
                        };
                        return memo;
                    }
                    case "showCostAwareValues":
                        return memo;
                    default:
                        memo.filter = { ...memo.filter, ...configs.bidFilterValues[filterKey][newFilters[filterKey]] };
                        return memo;
                }
            },
            { filter: {}, query: {} },
        );
    };

    mergeFilters = (data) => this.mergeMultipleFilters([data]);

    mergeCustomFilter = (filters, name, value) => {
        if (value === "-" || (!value && !mustHaveFalseKeys.includes(name)) || value.length === 0) {
            delete filters[name];
        } else {
            filters[name] = value;
        }
        return filters;
    };

    mergeMultipleFilters = (data) => {
        const filters = { ...this.props.filters };
        data.forEach(({ name, value }) => {
            this.mergeCustomFilter(filters, name, value);
        });
        const { mergeParams, params } = this.props;
        filters.isDeleted = filters.isDeleted === undefined ? false : filters.isDeleted;
        const buildFilters = this.buildOdataFilter(filters);
        params.customFilters = filters;
        params.preparedFilter = buildFilters.filter;
        params.queryFilter = buildFilters.query;
        setTimeout(() => {
            mergeFilters(mergeParams, { name: "custom", value: null }, params);
        }, 0);
    };

    swapPoints = () => {
        const fromValue = this.filterValue("from");
        const toValue = this.filterValue("to");
        const newFilters = { ...this.props.filters, from: toValue, to: fromValue };
        const { mergeParams, params } = this.props;
        const buildFilters = this.buildOdataFilter(newFilters);
        params.customFilters = newFilters;
        params.preparedFilter = buildFilters.filter;
        params.queryFilter = buildFilters.query;
        mergeFilters(mergeParams, { name: "custom", value: null }, params);
    };

    renderPointSelect = (propName) => {
        const filterValue = this.filterValue(propName);
        const pointFilters = normalizePointFilter(filterValue);
        const pointFilterIds = pointFilters.map(({ id }) => id);
        const selectedPointById = pointFilters.reduce((memo, item) => ({ ...memo, [item.id]: item }), {});
        const props = {
            onChange: ({ name, value, option }) => {
                const values = Array.isArray(value) ? value : value ? [value] : [];
                const options = Array.isArray(option) ? option : option ? [option] : [];
                const nextFilterValue = values
                    .map((id, index) => {
                        const selectedOption = options[index] || selectedPointById[id];
                        return selectedOption ? { id: selectedOption.id, type: selectedOption.type } : null;
                    })
                    .filter(Boolean);
                this.mergeMultipleFilters([{ name, value: nextFilterValue }]);
            },
            ...configs[propName === "from" ? "fromPointConfig" : "toPointConfig"](
                pointFilterIds.length ? pointFilterIds : null,
            ),
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
                {filterValue && this.radiusInput(propName)}
            </div>
        );
    };

    radiusInput = () => {
        return null;
        // const placement = propName === "from" ? "top" : "bottom";
        // return <input className={`b-filter__radius-input b-filter__input b-filter__radius-input--${placement}`} placeholder="+ км" />;
    };

    renderIdInput = () => {
        return (
            <div className="b-filter__body-cell">
                <input
                    className="b-filter__input"
                    placeholder="Номер заявки (ID, номер, номер в 1С)"
                    value={this.state.idInputValue}
                    name="id"
                    onChange={(e) => {
                        const value = e.target.value;
                        this.setState({ idInputValue: value });
                        this.onIdInputChange(value);
                    }}
                />
            </div>
        );
    };

    renderContractNumberInput = () => {
        return (
            <div className="b-filter__body-cell">
                <input
                    className="b-filter__input"
                    placeholder="Номер договора с контрагентом"
                    value={this.state.contractNumberInputValue}
                    name="contractNumber"
                    maxLength="100"
                    onChange={(e) => {
                        const value = e.target.value;
                        this.setState({ contractNumberInputValue: value });
                        this.onContractNumberInputChange(value);
                    }}
                />
            </div>
        );
    };

    renderLoadDateInput = () => {
        const propsFrom = {
            ...configs.loadDateConfig.from,
            onChange: this.mergeFilters,
            value: this.filterValue("loadFrom"),
        };
        const propsTo = {
            ...configs.loadDateConfig.to,
            onChange: this.mergeFilters,
            value: this.filterValue("loadBefore"),
        };
        return (
            <div className="b-filter__body-cell">
                <div className="b-filter__body-cell--left">
                    <Datetime {...propsFrom} />
                </div>
                <div className="b-filter__body-cell--right">
                    <Datetime {...propsTo} />
                </div>
            </div>
        );
    };

    renderUnloadDateInput = () => {
        const propsFrom = {
            ...configs.unloadDateConfig.from,
            onChange: this.mergeFilters,
            value: this.filterValue("unloadFrom"),
        };
        const propsTo = {
            ...configs.unloadDateConfig.to,
            onChange: this.mergeFilters,
            value: this.filterValue("unloadBefore"),
        };
        return (
            <div className="b-filter__body-cell">
                <div className="b-filter__body-cell--left b-filter__body-cell--topped">
                    <Datetime {...propsFrom} />
                </div>
                <div className="b-filter__body-cell--right b-filter__body-cell--topped">
                    <Datetime {...propsTo} />
                </div>
            </div>
        );
    };

    renderStatusSelect = () => {
        const props = {
            ...configs.statusConfig,
            onChange: this.mergeFilters,
            disabled: this.filterValue("isApproved") || this.filterValue("isUnapproved"),
            value: this.filterValue("status"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderPaymentStatusSelect = () => {
        if (!this.props.profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable)) {
            return null;
        }
        const props = {
            ...configs.paymentStatusConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("payment/paymentStatus"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderSyncStatusSelect = () => {
        const props = {
            ...configs.syncStatusConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("lastSyncStatus"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderLoadStatusSelect = () => {
        const props = {
            ...configs.loadStatusConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("bidPointLoadUnloadStatus"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderAcceptedByDriverSelect = () => {
        const props = {
            ...configs.acceptedByDriverConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("acceptedByDriver"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderHasUploadedFileSelect = () => {
        const props = {
            ...configs.hasUploadedFileConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("hasUploadedFile"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderWaybillStatusSelect = () => {
        if (!this.props.profile.availableFeatureModules.includes(featureModuleTypesEnum.Waybills)) {
            return null;
        }
        const props = {
            ...configs.waybillStatusConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("waybillStatus"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderWaybillSignatureRequiredSelect = () => {
        if (!this.props.profile.availableFeatureModules.includes(featureModuleTypesEnum.Waybills)) {
            return null;
        }
        const props = {
            ...configs.waybillSignatureRequiredConfig,
            onChange: ({ name, value, option }) => {
                const currentValue = this.filterValue(name);
                if (currentValue === value) {
                    this.mergeFilters({ name, value: null, option: null });
                } else {
                    this.mergeFilters({ name, value, option });
                }
            },
            value: this.filterValue("waybillSignatureRequired"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderLogistSelect = () => {
        const {
            organization: { showBidParticipantsFilterSeparately },
            profile: { availableFeatureModules },
        } = this.props;

        const defaultProps = {
            onChange: this.mergeFilters,
            styles: { ...customFilterSelectStyles },
        };

        const logistProps = {
            ...defaultProps,
            ...configs.logistConfig,
            value: this.filterValue("logistId"),
        };

        if (showBidParticipantsFilterSeparately) {
            const carLogistProps = {
                ...defaultProps,
                ...configs.carLogistConfig,
                value: this.filterValue("carLogistId"),
            };

            const responsibleProps = {
                ...defaultProps,
                ...configs.responsibleConfig,
                value: this.filterValue("responsibleId"),
            };

            const salesManagerProps = {
                ...defaultProps,
                ...configs.salesManagerConfig,
                value: this.filterValue("salesManagerId"),
            };
            return (
                <>
                    <div className="b-filter__body-cell">
                        <Select {...carLogistProps} />
                    </div>
                    <div className="b-filter__body-cell">
                        <Select {...responsibleProps} />
                    </div>
                    {availableFeatureModules.includes(featureModuleTypesEnum.BidSalesManager) && (
                        <div className="b-filter__body-cell">
                            <Select {...salesManagerProps} />
                        </div>
                    )}
                </>
            );
        }
        return (
            <div className="b-filter__body-cell">
                <Select {...logistProps} />
            </div>
        );
    };

    renderCounterpartySelect = () => {
        const props = {
            ...configs.counterpartyConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("counterpartyId"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderBidPointCounterpartySelect = (config) => {
        const name = config.inputProps.name;
        const props = {
            ...config,
            onChange: this.mergeFilters,
            value: getBidPointCounterpartyIds(this.filterValue(name)),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderCargoSenderCounterpartySelect = () =>
        this.renderBidPointCounterpartySelect(configs.cargoSenderCounterpartyConfig);

    renderCargoReceiverCounterpartySelect = () =>
        this.renderBidPointCounterpartySelect(configs.cargoReceiverCounterpartyConfig);

    renderTruckSelect = () => {
        const transportColumn = this.filterValue("transportColumn");
        const hasTransportColumn = Array.isArray(transportColumn) ? transportColumn.length > 0 : !!transportColumn;
        const props = {
            ...configs.carConfig,
            onChange: this.mergeMultipleFilters,
            dataIndex: hasTransportColumn ? "transportColumnId" : "id",
            value: this.filterValue("transportColumn") || this.filterValue("car"),
            values: {
                transportColumn: this.filterValue("transportColumn"),
                car: this.filterValue("car"),
            },
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <GroupedCarSelect {...props} />
            </div>
        );
    };

    renderTrailerSelect = () => {
        const props = {
            ...configs.trailerIdConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("trailerId"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderDriverSelect = () => {
        const props = {
            ...configs.driverConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("driverId"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderLegalPersonSelect = () => {
        const props = {
            ...configs.legalPersonConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("legalPersonId"),
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...props} />
            </div>
        );
    };

    renderTrailerTypeSelect = () => {
        const props = {
            ...configs.trailerConfig,
            onChange: this.mergeFilters,
            value: this.filterValue("trailerTypeId"),
            options: this.props.enums.trailerType,
            styles: { ...customFilterSelectStyles },
        };
        return (
            <div className="b-filter__body-cell">
                <Select {...this.selectProps} {...props} />
            </div>
        );
    };

    renderClamping = (name) => {
        const onChange = (e) => {
            const key = e.target.value;
            const value = this.state[name].value || "";
            this.setState({ [name]: { key, value } });
            value && this.mergeFilters({ name, value: { [key]: value } });
        };
        return (
            <select onChange={onChange} value={this.state[name].key} className="b-filter__clamping">
                <option value="ge">От</option>
                <option value="le">До</option>
            </select>
        );
    };

    renderEmptyMileageInput = () => {
        return (
            <div className="b-filter__body-cell">
                {this.renderClamping("emptyMileage")}
                <input
                    className="b-filter__input b-filter__input--left"
                    placeholder="Порожний пробег, км"
                    value={this.state.emptyMileage.value}
                    name="emptyMileage"
                    onChange={(e) => {
                        const key = this.state.emptyMileage.key;
                        const value = parseInt(e.target.value) || "";
                        const filterValue = { key, value };
                        this.setState({ emptyMileage: filterValue });
                        this.onEmptyMileageInputChange(value ? { [key]: value } : "");
                    }}
                />
            </div>
        );
    };

    renderOutputWithEmptyMileageConfigInput = () => {
        return (
            <div className="b-filter__body-cell">
                {this.renderClamping("outputWithEmptyMileage")}
                <input
                    className="b-filter__input b-filter__input--left"
                    placeholder={`${upperFirst(this.props.organization?.currencyData?.name)}/км с порожним`}
                    value={this.state.outputWithEmptyMileage.value}
                    name="outputWithEmptyMileage"
                    onChange={(e) => {
                        const key = this.state.outputWithEmptyMileage.key;
                        const value = parseInt(e.target.value) || "";
                        const filterValue = { key, value };
                        this.setState({ outputWithEmptyMileage: filterValue });
                        this.onOutputWithEmptyMileageInputChange(value ? { [key]: value } : "");
                    }}
                />
            </div>
        );
    };

    renderTagItem = ({ name, selected, onChange, disabled }, index) => {
        const cname = cn("b-filter__tag--wrap", {
            "b-filter__tag--wrap-selected": selected,
            "b-filter__tag--wrap-disabled": disabled,
        });
        return (
            <div className={cname} onClick={() => !disabled && onChange()} key={index}>
                {selected ? <span className="b-filter__tag--delete" /> : <span className="b-filter__tag--checkbox" />}
                <span className="b-filter__tag--text">{name}</span>
            </div>
        );
    };

    refreshData = (e) => {
        e.stopPropagation();
        this.props.mergeParams();
    };

    resetFilters = (e) => {
        e && e.stopPropagation();
        const { mergeParams, params } = this.props;
        params.customFilters = {};
        params.preparedFilter = { isDeleted: false };
        params.queryFilter = {};
        this.setState({ idInputValue: "", contractNumberInputValue: "", emptyMileage: "", outputWithEmptyMileage: "" });
        mergeFilters(mergeParams, { name: "custom", value: null }, params);
    };

    render() {
        const {
            filters: { isDeleted, ...filters },
            filtersOpened,
            toggleFilter,
        } = this.props;
        const cname = cn("b-filter__body-double-cell--inputs", {
            "b-filter__body-double-cell--inputs-with-button": this.filterValue("from") && this.filterValue("to"),
        });
        const expandCname = cn("b-filter__collapse-btn", {
            "b-filter__collapse-btn--top": filtersOpened,
            "b-filter__collapse-btn--bottom": !filtersOpened,
        });
        const headerTextCname = cn("b-filter__search-text", {
            "b-filter__search-text--dark": filtersOpened,
        });
        return (
            <div className="b-filter">
                <div className="b-filter__head" onClick={toggleFilter}>
                    <div className="b-filter__head--left">
                        {!filtersOpened && <div className="b-filter__search-icon" />}
                        <div className={headerTextCname}>Найти заявку</div>
                    </div>
                    <div className="b-filter__head--right">
                        {!filtersOpened && (
                            <>
                                {(Object.keys(filters).length !== 0 ||
                                    (this.props.roles.isTruckingOrganizationAdmin && isDeleted)) && (
                                    <div className="b-filter__btn b-filter__btn--reset" onClick={this.resetFilters}>
                                        Сбросить
                                    </div>
                                )}
                                <div className="b-filter__btn b-filter__btn--refresh" onClick={this.refreshData}>
                                    Обновить
                                </div>
                            </>
                        )}
                        <div className={expandCname} />
                    </div>
                </div>
                {filtersOpened && (
                    <>
                        <div className="b-filter__body">
                            <div className="b-filter__body-column">
                                <div className="b-filter__body-double-cell">
                                    <div className={cname}>
                                        {this.renderPointSelect("from")}
                                        {this.renderPointSelect("to")}
                                    </div>
                                    {this.filterValue("from") && this.filterValue("to") && (
                                        <div className="b-filter__body-double-cell--button-wrap">
                                            <div
                                                className="b-filter__body-double-cell--button"
                                                onClick={this.swapPoints}
                                            />
                                        </div>
                                    )}
                                </div>
                                {this.renderIdInput()}
                                {this.renderContractNumberInput()}
                                {this.renderTruckSelect()}
                                {this.renderTrailerSelect()}
                                {this.renderDriverSelect()}
                                {this.renderStatusSelect()}
                                {this.renderPaymentStatusSelect()}
                                {this.state.legalPersonCount >= 2 && this.renderLegalPersonSelect()}
                            </div>
                            <div className="b-filter__body-column">
                                {this.renderLoadDateInput()}
                                {this.renderUnloadDateInput()}
                                {this.renderCounterpartySelect()}
                                {this.renderCargoSenderCounterpartySelect()}
                                {this.renderCargoReceiverCounterpartySelect()}
                                {this.renderLogistSelect()}
                                {this.renderAcceptedByDriverSelect()}
                                {this.renderLoadStatusSelect()}
                                {this.renderSyncStatusSelect()}
                                {this.renderHasUploadedFileSelect()}
                                {this.renderWaybillStatusSelect()}
                                {this.renderWaybillSignatureRequiredSelect()}
                            </div>
                            <div className="b-filter__body-column">{this.renderTagInputs()}</div>
                        </div>
                        <div className="b-filter__footer">
                            {(Object.keys(filters).length !== 0 ||
                                (this.props.roles.isTruckingOrganizationAdmin && isDeleted)) && (
                                <div className="b-filter__btn b-filter__btn--reset" onClick={this.resetFilters}>
                                    Сбросить
                                </div>
                            )}
                            <div className="b-filter__btn b-filter__btn--refresh" onClick={this.refreshData}>
                                Обновить
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}
